import { motion } from 'motion/react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Trophy, Flag, Calendar, MessageSquare, Settings, Radio } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLeagueStandings } from '../hooks/queries/useLeagueStandings';
import { useLiveRaceEvent } from '../hooks/queries/useLiveRaceEvent';

interface Tab {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  isActive: boolean;
}

export function LeagueTabBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ leagueId?: string }>();
  const { user } = useAuth();

  const leagueId =
    params.leagueId ||
    location.pathname.match(/\/standings\/([^/]+)/)?.[1] ||
    location.pathname.match(/\/leagues\/([^/]+)/)?.[1];

  const { data: standings } = useLeagueStandings(leagueId);
  const { data: liveEvent } = useLiveRaceEvent(standings?.leagueTypeId);

  const isCommissioner =
    !!standings?.commissionerUserId && standings.commissionerUserId === user?.userId;

  const hasLiveRace = !!liveEvent;

  if (!leagueId) return null;

  const path = location.pathname;

  const tabs: Tab[] = [
        ...(hasLiveRace && liveEvent
      ? [
        {
          key: 'live',
          label: 'Live',
          icon: <Radio size={20} />,
          path: `/leagues/${leagueId}/events/${liveEvent.eventId}/live`,
          isActive: path === `/leagues/${leagueId}/events/${liveEvent.eventId}/live`,
        },
      ]
      : []),
    {
      key: 'standings',
      label: 'Standings',
      icon: <Trophy size={20} />,
      path: `/standings/${leagueId}`,
      isActive: path.startsWith(`/standings/${leagueId}`),
    },
    {
      key: 'results',
      label: 'Results',
      icon: <Flag size={20} />,
      path: `/leagues/${leagueId}/events/results`,
      isActive: path === `/leagues/${leagueId}/events/results`,
    },
    {
      key: 'upcoming',
      label: 'Upcoming',
      icon: <Calendar size={20} />,
      path: `/leagues/${leagueId}/events/upcoming`,
      isActive: path === `/leagues/${leagueId}/events/upcoming`,
    },
    {
      key: 'messages',
      label: 'Messages',
      icon: <MessageSquare size={20} />,
      path: `/leagues/${leagueId}/messages`,
      isActive: path.startsWith(`/leagues/${leagueId}/messages`),
    },
    ...(isCommissioner
      ? [
        {
          key: 'manage',
          label: 'Manage',
          icon: <Settings size={20} />,
          path: `/leagues/${leagueId}/manage`,
          isActive: path.startsWith(`/leagues/${leagueId}/manage`),
        },
      ]
      : []),
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-sm border-t border-border/60"
      role="tablist"
      aria-label="League navigation"
    >
      <div className="flex items-stretch justify-around h-14 pb-safe">
        {tabs.map(tab => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={tab.isActive}
            onClick={() => navigate(tab.path)}
            className="relative flex flex-col items-center justify-center gap-1 flex-1 text-xs font-medium transition-colors focus-visible:outline-none"
            style={{
              color: tab.isActive
                ? 'hsl(var(--primary))'
                : 'hsl(var(--muted-foreground))',
            }}
          >
            {tab.isActive && (
              <motion.div
                layoutId="league-tab-indicator"
                className="absolute top-0 left-2 right-2 h-0.5 bg-primary rounded-full"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <div className="relative">
              {tab.icon}
              {tab.key === 'live' && (
                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
              )}
            </div>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}
